import * as React from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {Spinner} from '@frontrow/console/src/components/spinner'
import {Dialog, DialogBody, DialogHeading} from '@frontrow/console/src/components/ui/dialog'
import {Text} from '@frontrow/console/src/components/typography'
import {type OperationLocationT} from '@frontrow/console/src/entities/helpers/operation'
import {useSafeEffectExtraDeps, type CallbackFn} from '@freckle/react-hooks'
import {type GroupedArtifactData} from '@frontrow/console/src/entities/models/artifact'

import {useOperation, type ArtifactDataState} from '@frontrow/console/src/hooks/use-operation'

type Props<TOperationLocation> = {
  operationLocation: TOperationLocation
  title: string
  heading: string
  body: string
  onSuccess: CallbackFn<(a: GroupedArtifactData) => void>
  onFailure: CallbackFn<() => void>
}

const PendingOperation = ({
  operationLocation,
  onSuccess,
  onFailure,
  title,
  heading,
  body
}: Props<OperationLocationT>): React.ReactElement | null => {
  const {operation, artifactData} = useOperation({operationLocation})

  const operationStatus = operation?.status

  useSafeEffectExtraDeps(
    ({onFailure}) => {
      switch (operationStatus) {
        case null:
        case undefined:
        case 'not_started':
        case 'running':
        case 'succeeded':
          return
        case 'failed':
          return (onFailure as () => void)()
        default:
          return exhaustive(operationStatus)
      }
    },
    [operationStatus],
    {onSuccess, onFailure}
  )

  useSafeEffectExtraDeps(
    ({
      onSuccess,
      onFailure,
      artifactData
    }: {
      onSuccess: (a: GroupedArtifactData) => void
      onFailure: () => void
      artifactData: ArtifactDataState
    }) => {
      switch (artifactData.tag) {
        case 'fetching':
        case 'not_started':
          return
        case 'success':
          return onSuccess(artifactData.artifactData)
        case 'failed':
          return onFailure()
        default:
          return exhaustive(artifactData as never)
      }
    },
    [],
    {
      onSuccess,
      onFailure,
      artifactData: {
        value: artifactData,
        comparator: (a1, a2) => a1.tag === a2.tag
      }
    }
  )

  switch (operationStatus) {
    case null:
    case undefined:
    case 'not_started':
    case 'running':
      return <Modal title={title} heading={heading} body={body} />
    case 'succeeded':
      switch (artifactData.tag) {
        case 'fetching':
        case 'not_started':
          return <Modal title={title} heading={heading} body={body} />
        case 'success':
        case 'failed':
          return null
        default:
          return exhaustive(artifactData)
      }
    case 'failed':
      return null
    default:
      return exhaustive(operationStatus)
  }
}

const Modal = ({
  title,
  heading,
  body
}: {
  title: string
  heading: string
  body: string
}): React.ReactElement => {
  return (
    <Dialog open hideOnEscape={false}>
      <DialogHeading dismissible={false}>{title}</DialogHeading>
      <DialogBody>
        <div className="flex flex-col">
          <Spinner />
          <Text style="subtitle-2">{heading}</Text>
          <Text style="body-2">{body}</Text>
        </div>
      </DialogBody>
    </Dialog>
  )
}

export const PendingOperationWhenLocation = ({
  operationLocation,
  ...rest
}: Props<OperationLocationT | undefined | null>): React.ReactElement | null =>
  operationLocation === null || operationLocation === undefined
    ? null
    : PendingOperation({...rest, operationLocation})
